const Joi = require('joi');
const customerData = require('../../../common/server/schema/customerData');
const buildingData = require('../../../common/server/schema/buildingData');
const {allProductsRequiringSerial} = require('../../../common/client/js/utils/serialNumberProductsLists')
const MIN_DAYS_IN_ADVANCE = 3;

const appointmentSchema = Joi.date()
  .greater('now')
  .custom((value, helpers) => {
    const day = value.getDay();

    // Normalize both dates to midnight (ignore time)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 3); // exactly 3 days from today

    const normalizedValue = new Date(value);
    normalizedValue.setHours(0, 0, 0, 0);

    // Check if at least 3 days in advance
    if (normalizedValue < minDate) {
      return helpers.error('date.minAdvance');
    }

    // Check if not Saturday (6) or Sunday (0)
    if (day === 0 || day === 6) {
      return helpers.error('date.noWeekends');
    }

    return value;
  })
  .messages({
    'date.base': 'errors.date.invalid',
    'date.greater': 'errors.date.past',
    'date.minAdvance': 'errors.date.tooSoon',
    'date.noWeekends': 'errors.date.weekend',
  });

const defectiveDevicesSchema = Joi.object({
  articleNumber: Joi.string().trim().required().messages({
    'string.empty': 'errors.articleNumber.required',
    'any.required': 'errors.articleNumber.required'
  }),

  serialNumber: Joi.string().trim().when('articleNumber', {
    // If articleNumber is one of the known options that DO require serial
    is: Joi.valid('',...allProductsRequiringSerial),
    then: Joi.required().messages({
      'string.empty': 'errors.serial.required',
      'any.required': 'errors.serial.required'
    }),
    // Otherwise → no serial required
    otherwise: Joi.optional().allow('').strip()
  }),

  errorDescription: Joi.string().trim().required().messages({
    'string.empty': 'errors.errorDescription.required',
    'any.required': 'errors.errorDescription.required'
  })
});

// Billing address fields are only required when it differs from the order address
const billingField = (label, extraRules = (schema) => schema) =>
  Joi.string().trim().when('billingAddressDiffers', {
    is: true,
    then: extraRules(Joi.string().trim().required()).messages({
      'string.empty': `errors.billing.${label}.required`,
      'string.pattern.base': `errors.billing.${label}.invalid`,
      'any.required': `errors.billing.${label}.required`
    }),
    otherwise: Joi.allow('').optional()
  });

const customerServiceSchemaExtra = Joi.object({
  // Customer type and VAT ID are only asked for new customers (no customer number)
  customerType: Joi.string().trim().when('hasNoCustomerNumber', {
    is: true,
    then: Joi.valid('private', 'business').required().messages({
      'any.only': 'errors.customerType.required',
      'string.empty': 'errors.customerType.required',
      'any.required': 'errors.customerType.required'
    }),
    otherwise: Joi.allow('').optional()
  }),
  vatId: Joi.string().trim().when('customerType', {
    is: 'business',
    then: Joi.string().trim().uppercase().pattern(/^DE\d{9}$/).required().messages({
      'string.empty': 'errors.vatId.required',
      'string.pattern.base': 'errors.vatId.invalid',
      'any.required': 'errors.vatId.required'
    }),
    otherwise: Joi.allow('').optional()
  }),
  billingAddressDiffers: Joi.boolean().default(false),
  billingName: billingField('name'),
  billingStreet: billingField('street'),
  billingHouseNumber: billingField('houseNumber'),
  billingPostalCodeNumber: billingField('postalCode', (schema) => schema.pattern(/^\d{5}$/)),
  billingCity: billingField('city'),
  invoiceEmail: Joi.string().trim().email({ tlds: { allow: false } }).required().messages({
    'string.empty': 'errors.invoiceEmail.required',
    'string.email': 'errors.invoiceEmail.invalid',
    'any.required': 'errors.invoiceEmail.required'
  }),
  hasNoWarrantyNumber: Joi.boolean().default(false),
  dateOfPurchase: Joi.date().allow(''),
  receiptUpload: Joi.object({
    fieldname: Joi.string().required(),
    originalname: Joi.string().required(),
    encoding: Joi.string().required(),
    mimetype: Joi.string().required(),
    destination: Joi.string().allow('').optional(),
    size: Joi.number().positive().required(),
  }),
  warrantyNumber: Joi.string().trim().when('hasNoWarrantyNumber', {
    is: false,
    then: Joi.required().messages({
      'string.empty': 'errors.warranty.number.required',
      'any.required': 'errors.warranty.number.required'
    }),
    otherwise: Joi.optional().allow('')
  }),
  documentation: Joi.string().trim().when('hasNoWarrantyNumber', {
    is: false,
    then: Joi.optional().messages({
      'string.empty': 'errors.documentation.required',
      'any.required': 'errors.documentation.required'
    }),
    otherwise: Joi.optional()
  }),
  guarantee: Joi.string().trim().when('hasNoWarrantyNumber', {
    is: false,
    then: Joi.optional().messages({
      'string.empty': 'errors.guarantee.required',
      'any.required': 'errors.guarantee.required'
    }),
    otherwise: Joi.optional()
  }),
  installationDate: Joi.date().optional().allow(''),
  installer: Joi.string().trim().optional().allow(''),
  defectiveDeviceCount: Joi.number().integer().optional().allow('', null).default(0),
  // serviceType: Joi.string().trim().when('hasNoWarrantyNumber', {
  //   is: false,
  //   then: Joi.required().messages({
  //     'string.empty': 'errors.serviceType.required',
  //     'any.required': 'errors.serviceType.required'
  //   }),
  //   otherwise: Joi.optional()
  // }),
  onlineServiceType: Joi.string().trim().allow(''),
  asap: Joi.boolean().default(false),
  appointment1: appointmentSchema.optional().allow(''),
  appointment2: appointmentSchema.optional().allow(''),
  comments: Joi.string().trim().allow(''),
  contactPerson: Joi.string().trim().when('hasNoCustomerNumber', {
    is: false,
    then: Joi.required().messages({
      'string.empty': 'errors.contactPerson.required',
      'any.required': 'errors.contactPerson.required'
    }),
    otherwise: Joi.allow('').optional()
  }),
  errorDescription: Joi.string().min(20).required().messages({
    'string.empty': 'errors.errorDescription.required',
    'any.required': 'errors.errorDescription.required',
    'string.length': 'errors.errorDescription.length'
  }),
  Bauvorhaben: Joi.string().allow(''),
  devices: Joi.array().items(Joi.object({
    articleNumber: Joi.string().trim().required().messages({
      'string.empty': 'errors.devices.articleNumber.required',
      'any.required': 'errors.devices.articleNumber.required'
    }),
    quantity: Joi.number().integer().min(1).required().messages({
      'number.base': 'errors.devices.quantity.invalid',
      'number.min': 'errors.devices.quantity.min'
    })
  })).optional(),
  defectiveDevices: Joi.array().items(defectiveDevicesSchema).optional(),
}).options({ abortEarly: false });

const customerServiceSchema = customerServiceSchemaExtra.concat(customerData).concat(buildingData);
module.exports = customerServiceSchema;
